import { ChevronLeft, Pencil, ScanSearch } from 'lucide-react'
import { useCallback, useEffect, useRef, useState, type ReactNode } from 'react'
import { Link, useLocation, useNavigate, useParams, useSearchParams } from 'react-router-dom'

import { Combobox } from '@/components/Combobox'
import { DataTable, type Column } from '@/components/data/DataTable'
import {
  DetailPane, DetailSection, KeyValue, detailActionClass, detailDangerClass,
} from '@/components/data/DetailPane'
import { FilterBar } from '@/components/data/FilterBar'
import { LoadingRows } from '@/components/data/SearchInput'
import { SplitView } from '@/components/data/SplitView'
import { StatTiles, type Tile } from '@/components/data/StatTiles'
import { StatusText } from '@/components/data/StatusText'
import { SectionBoundary } from '@/components/feedback/SectionBoundary'
import { Page } from '@/components/layout/Page'
import { UserActionDialog } from '@/components/UserActionDialog'
import { api } from '@/lib/api'
import { useSelection } from '@/lib/useSelection'

type User = { account_id: string; email: string | null; display_name: string; active: boolean }
type OrgStatus = {
  account_type: string | null
  account_status: string | null
  managed: boolean
  last_active: string | null
}
type ApiToken = { id: string; label: string | null; created_at: string | null; last_access: string | null }
type ProductRole = { key: string; name: string | null }
type OrgRoleAssignment = { role: string; resource_id?: string | null; resource_type?: string | null }
type PermittedProject = { key: string; name: string | null }
type Group = { name: string }

type DialogState = {
  op: string
  params: Record<string, unknown>
  title: string
  critical?: boolean
  onDone?: () => void
}

/** A section's data: null — loading; { error } — failed ('token' = the actor lacks the key). */
type Loaded<T> = T | null | { error: string }
const isErr = <T,>(x: Loaded<T>): x is { error: string } =>
  !!x && typeof x === 'object' && !Array.isArray(x) && 'error' in x

// Classify a failed section load. `api()` throws `Error("<status> <body>")`, so
// a 503 means the actor lacks the required key/token → show the Settings hint.
// Anything else is a real failure we surface verbatim instead of blaming tokens.
function loadError(e: unknown): string {
  const msg = e instanceof Error ? e.message : String(e)
  return /^503\b/.test(msg) ? 'token' : msg || 'request failed'
}

const TABS = [
  { key: 'groups', label: 'Groups' },
  { key: 'products', label: 'Product access' },
  { key: 'roles', label: 'Org roles' },
  { key: 'tokens', label: 'API tokens' },
  { key: 'projects', label: 'Projects' },
  { key: 'account', label: 'Account' },
] as const
type TabKey = (typeof TABS)[number]['key']
const toTab = (v: string | null): TabKey => TABS.find((t) => t.key === v)?.key ?? 'groups'

const PANEL_ID = 'user-panel'
const TAB_PREFIX = 'user-tab'

const KEY_HINT: Record<string, string> = {
  jira: 'Jira token required (Settings → Tokens).',
  org: 'Needs an org-admin key (Settings → Tokens).',
}

function errText(error: string, key: 'jira' | 'org') {
  return error === 'token' ? KEY_HINT[key] : error
}

function listTile<T>(key: TabKey, label: string, data: Loaded<T[]>, keyKind: 'jira' | 'org', meta: string): Tile {
  if (data === null) return { key, label, value: null, state: 'loading' }
  if (isErr(data)) {
    return data.error === 'token'
      ? { key, label, value: '—', meta: keyKind === 'org' ? 'needs org-admin key' : 'needs Jira token' }
      : { key, label, value: null, state: 'failed', meta: 'not loaded' }
  }
  if (!Array.isArray(data)) return { key, label, value: null, state: 'failed', meta: 'unexpected data' }
  return { key, label, value: data.length, meta }
}

/** Table section with an optional strip; never crashes on data of the wrong shape. */
function ListSection<T>({ data, keyKind, strip, columns, rowId, label, empty, detail }: {
  data: Loaded<T[]>
  keyKind: 'jira' | 'org'
  strip?: ReactNode
  columns: Column<T>[]
  rowId: (r: T) => string
  label: string
  empty: string
  detail?: (row: T) => ReactNode
}) {
  const [sel, setSel] = useSelection()
  const rows = Array.isArray(data) ? data : []
  const selected = rows.find((r) => rowId(r) === sel) ?? null
  return (
    <div className="flex min-h-0 flex-1 flex-col">
      {strip && <FilterBar>{strip}</FilterBar>}
      {data === null ? <LoadingRows label={`Loading ${label.toLowerCase()}`} rows={4} />
        : isErr(data) ? <p role="status" className="px-4 py-4 text-[13px] text-warn">Couldn’t load {label.toLowerCase()} — {errText(data.error, keyKind)}</p>
        : !Array.isArray(data) ? <p role="alert" className="px-4 py-4 text-[13px] text-danger">Couldn’t load {label.toLowerCase()} — unexpected data.</p>
        : (
          <SplitView
            onCloseDetail={() => setSel(null)}
            detail={selected && detail ? detail(selected) : null}
            list={<DataTable label={label} rows={rows} columns={columns} rowId={rowId} selectedId={sel} onSelect={setSel} empty={empty} />}
          />
        )}
    </div>
  )
}

const date = (iso: string | null) => (iso ? new Date(iso).toLocaleDateString() : '—')

export default function UserDetail() {
  const { accountId = '' } = useParams()
  // Remount per user: the /users/:accountId route re-uses this element across
  // user→user navigations (e.g. via the command palette), and every piece of
  // state carried over — identity, section data, open dialogs — belongs to the
  // PREVIOUS user, while group/disable mutations derive their params from it.
  return <UserDetailInner key={accountId} accountId={accountId} />
}

function UserDetailInner({ accountId }: { accountId: string }) {
  const location = useLocation() as { state?: { user?: User } }
  const navigate = useNavigate()
  const [params, setParams] = useSearchParams()
  const tab = toTab(params.get('tab'))
  const go = (t: string) => setParams((p) => {
    const next = new URLSearchParams(p)
    next.set('tab', t)
    next.delete('sel')
    return next
  }, { replace: true })

  // navigation state may be stale or forged by a link — only trust it for this account
  const carried = location.state?.user
  const [user, setUser] = useState<User | null>(carried && carried.account_id === accountId ? carried : null)
  const [groups, setGroups] = useState<Loaded<Group[]>>(null)
  const [org, setOrg] = useState<Loaded<OrgStatus>>(null)
  const [tokens, setTokens] = useState<Loaded<ApiToken[]>>(null)
  const [products, setProducts] = useState<Loaded<ProductRole[]>>(null)
  const [orgRoles, setOrgRoles] = useState<Loaded<OrgRoleAssignment[]>>(null)
  const [projects, setProjects] = useState<Loaded<PermittedProject[]>>(null)
  const [editingName, setEditingName] = useState<string | null>(null)
  const [transferTarget, setTransferTarget] = useState('')
  const [addGroupValue, setAddGroupValue] = useState('')
  const [dialog, setDialog] = useState<DialogState | null>(null)
  const editInputRef = useRef<HTMLInputElement>(null)

  const load = useCallback(<T,>(path: string, set: (v: Loaded<T>) => void) => {
    set(null)
    api<T>(`/users/${encodeURIComponent(accountId)}/${path}`)
      .then(set)
      .catch((e) => set({ error: loadError(e) }))
  }, [accountId])

  const refreshGroups = useCallback(() => load<Group[]>('groups', setGroups), [load])
  const refreshOrg = useCallback(() => load<OrgStatus>('org-status', setOrg), [load])
  const refreshTokens = useCallback(() => load<ApiToken[]>('api-tokens', setTokens), [load])

  useEffect(() => {
    // if the user wasn't carried in navigation state, look it up by account_id
    if (!user) {
      api<unknown>(`/users?q=${encodeURIComponent(accountId)}`)
        .then((r) => setUser(Array.isArray(r) ? (r as User[]).find((u) => u.account_id === accountId) ?? null : null))
        .catch(() => {})
    }
    refreshGroups()
    refreshOrg()
    refreshTokens()
    load<ProductRole[]>('product-access', setProducts)
    // org roles — the route resolves directory_id server-side (sends only account_id)
    load<OrgRoleAssignment[]>('org-roles', setOrgRoles)
    // permitted projects — the route bulk-checks BROWSE for this specific user
    load<PermittedProject[]>('permitted-projects', setProjects)
  }, [accountId]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    if (editingName !== null) {
      editInputRef.current?.focus()
      editInputRef.current?.select()
    }
  }, [editingName !== null]) // eslint-disable-line react-hooks/exhaustive-deps

  const orgOk = org && !isErr(org) ? org : null

  function commitNameEdit() {
    if (editingName !== null && editingName.trim() !== '' && editingName.trim() !== user?.display_name) {
      setDialog({
        op: 'users.update_profile',
        params: { account_id: accountId, name: editingName.trim() },
        title: 'Rename user',
        onDone: () => {
          setUser((u) => (u ? { ...u, display_name: editingName!.trim() } : u))
          setEditingName(null)
        },
      })
    } else {
      setEditingName(null)
    }
  }

  const accountTile: Tile = org === null
    ? { key: 'account', label: 'Account', value: null, state: 'loading' }
    : isErr(org)
      ? (org.error === 'token'
        ? { key: 'account', label: 'Account', value: '—', meta: 'needs org-admin key' }
        : { key: 'account', label: 'Account', value: null, state: 'failed', meta: 'not loaded' })
      : { key: 'account', label: 'Account', value: org.account_status ?? '—', meta: org.managed ? 'managed account' : 'not managed' }

  const tiles: Tile[] = [
    listTile('groups', 'Groups', groups, 'jira', 'Jira groups'),
    listTile('products', 'Product access', products, 'jira', 'products'),
    listTile('roles', 'Org roles', orgRoles, 'org', 'assigned roles'),
    listTile('tokens', 'API tokens', tokens, 'org', 'tokens'),
    listTile('projects', 'Projects', projects, 'jira', 'can browse'),
    accountTile,
  ]

  const groupColumns: Column<Group>[] = [{ key: 'name', header: 'Group', mono: true }]
  const productColumns: Column<ProductRole>[] = [
    { key: 'name', header: 'Product', render: (p) => p.name ?? p.key, title: (p) => p.name ?? p.key },
    { key: 'key', header: 'Key', mono: true, width: '40%' },
  ]
  const roleColumns: Column<OrgRoleAssignment>[] = [
    { key: 'role', header: 'Role', mono: true },
    { key: 'resource_type', header: 'Resource type', width: '24%', render: (r) => r.resource_type ?? 'organization' },
    { key: 'resource_id', header: 'Resource id', mono: true, width: '30%', render: (r) => r.resource_id ?? '—' },
  ]
  const tokenColumns: Column<ApiToken>[] = [
    { key: 'label', header: 'Token', mono: true, render: (t) => t.label ?? t.id, title: (t) => t.label ?? t.id },
    { key: 'created_at', header: 'Created', mono: true, width: '20%', render: (t) => date(t.created_at) },
    { key: 'last_access', header: 'Last access', mono: true, width: '20%', render: (t) => date(t.last_access) },
  ]
  const projectColumns: Column<PermittedProject>[] = [
    { key: 'key', header: 'Project', mono: true, width: '140px' },
    { key: 'name', header: 'Name', render: (p) => p.name ?? '—' },
  ]

  const statusWord = user ? (user.active ? 'active' : 'inactive') : null

  return (
    <Page
      title={user?.display_name ?? accountId}
      context={(
        <span className="flex min-w-0 items-center gap-2.5 text-[12px]">
          {orgOk?.managed && editingName === null && (
            <button
              type="button"
              aria-label="Edit name"
              title="Rename (managed account)"
              onClick={() => setEditingName(user?.display_name ?? accountId)}
              className="rounded p-1 text-text-2 transition-colors hover:bg-surface-raised hover:text-foreground"
            >
              <Pencil aria-hidden className="size-3.5" />
            </button>
          )}
          {editingName !== null && (
            <span className="flex items-center gap-1.5">
              <input
                ref={editInputRef}
                value={editingName}
                onChange={(e) => setEditingName(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === 'Enter') commitNameEdit()
                  if (e.key === 'Escape') setEditingName(null)
                }}
                aria-label="Edit display name"
                className="h-7 w-56 rounded-md border border-input bg-surface px-2 text-[13px] text-foreground"
              />
              <button type="button" onClick={commitNameEdit} className={detailActionClass}>Save</button>
              <button type="button" onClick={() => setEditingName(null)} className="px-1 text-[12px] text-text-2 hover:text-foreground">
                Cancel
              </button>
            </span>
          )}
          {statusWord && <StatusText status={statusWord} />}
          {user?.email && <span className="truncate font-mono text-text-2">{user.email}</span>}
          <span className="truncate font-mono text-[11px] text-text-3" title={accountId}>{accountId}</span>
          <Link to="/users" className="inline-flex shrink-0 items-center gap-0.5 text-text-3 transition-colors hover:text-foreground">
            <ChevronLeft aria-hidden className="size-3.5" /> All users
          </Link>
        </span>
      )}
      actions={(
        <>
          <Link to={`/analysis/user/${encodeURIComponent(accountId)}`} className={detailActionClass}>
            <ScanSearch aria-hidden className="size-3.5" /> Where used
          </Link>
          {orgOk?.managed && user && (orgOk.account_status === 'active'
            ? (
              <button
                type="button"
                className={detailDangerClass}
                onClick={() => setDialog({
                  op: 'users.disable_managed_account',
                  params: { account_ids: [accountId] },
                  title: `Disable ${user.display_name}`,
                  critical: true,
                  onDone: refreshOrg,
                })}
              >
                Disable account
              </button>
            )
            : (
              <button
                type="button"
                className={detailActionClass}
                onClick={() => setDialog({
                  op: 'users.restore_managed_account',
                  params: { account_ids: [accountId] },
                  title: `Restore access for ${user.display_name}`,
                  onDone: refreshOrg,
                })}
              >
                Restore access
              </button>
            ))}
        </>
      )}
    >
      <StatTiles tiles={tiles} active={tab} onSelect={go} label="User sections" idPrefix={TAB_PREFIX} panelId={PANEL_ID} />
      <div role="tabpanel" id={PANEL_ID} aria-labelledby={`${TAB_PREFIX}-${tab}`} className="flex min-h-0 flex-1 flex-col">
        {tab === 'groups' && (
          <SectionBoundary key="groups" label="Groups" onRetry={refreshGroups} resetKeys={[groups]}>
            <ListSection
              data={groups}
              keyKind="jira"
              label="Groups"
              columns={groupColumns}
              rowId={(g) => g.name}
              empty="No groups."
              strip={(
                <span className="flex items-center gap-2">
                  <span className="text-[12px] text-text-2">Add to group</span>
                  <span className="w-64">
                    <Combobox
                      source="group"
                      valueKey="name"
                      deps={{}}
                      multi={false}
                      value={addGroupValue}
                      onChange={setAddGroupValue}
                      ariaLabel="Add to group"
                      disabled={!user?.email}
                      className=""
                      onSelect={(g) => {
                        if (g && user?.email) {
                          setDialog({
                            op: 'users.bulk_assign_groups',
                            params: { emails: [user.email], group_names: [g] },
                            title: `Add ${user.display_name} to ${g}`,
                          })
                          setAddGroupValue('')
                        }
                      }}
                    />
                  </span>
                  {!user?.email && <span className="text-[12px] text-text-3">needs the user’s email</span>}
                </span>
              )}
              detail={(g) => (
                <DetailPane
                  eyebrow="Group"
                  title={<span className="font-mono">{g.name}</span>}
                  actions={user?.email && (
                    <button
                      type="button"
                      className={detailDangerClass}
                      onClick={() => setDialog({
                        op: 'users.remove_from_group',
                        params: { group_name: g.name, emails: [user.email!] },
                        title: `Remove from ${g.name}`,
                      })}
                    >
                      Remove from group
                    </button>
                  )}
                />
              )}
            />
          </SectionBoundary>
        )}
        {tab === 'products' && (
          <SectionBoundary key="products" label="Product access" resetKeys={[products]}>
            <ListSection data={products} keyKind="jira" label="Product access" columns={productColumns}
              rowId={(p) => p.key} empty="No product access." />
          </SectionBoundary>
        )}
        {tab === 'roles' && (
          <SectionBoundary key="roles" label="Org roles" resetKeys={[orgRoles]}>
            <ListSection
              data={orgRoles}
              keyKind="org"
              label="Org roles"
              columns={roleColumns}
              rowId={(r) => `${r.role}|${r.resource_id ?? 'org'}`}
              empty="No org roles assigned."
              strip={(
                <button
                  type="button"
                  className={detailActionClass}
                  onClick={() => navigate(`/operations/users.assign_org_role?account_id=${encodeURIComponent(accountId)}`)}
                >
                  Assign role
                </button>
              )}
              detail={(r) => (
                <DetailPane
                  eyebrow="Org role"
                  title={<span className="font-mono">{r.role}</span>}
                  actions={(
                    <button
                      type="button"
                      className={detailDangerClass}
                      onClick={() => navigate(
                        `/operations/users.revoke_org_role?account_id=${encodeURIComponent(accountId)}&role=${encodeURIComponent(r.role)}`,
                      )}
                    >
                      Revoke role
                    </button>
                  )}
                >
                  <KeyValue items={[
                    ['Resource type', r.resource_type ?? 'organization'],
                    ['Resource id', <span className="font-mono text-[12px]">{r.resource_id ?? '—'}</span>],
                  ]} />
                </DetailPane>
              )}
            />
          </SectionBoundary>
        )}
        {tab === 'tokens' && (
          <SectionBoundary key="tokens" label="API tokens" onRetry={refreshTokens} resetKeys={[tokens]}>
            <ListSection
              data={tokens}
              keyKind="org"
              label="API tokens"
              columns={tokenColumns}
              rowId={(t) => t.id}
              empty="No API tokens."
              detail={(t) => (
                <DetailPane
                  eyebrow="API token"
                  title={<span className="font-mono">{t.label ?? t.id}</span>}
                  actions={(
                    <button
                      type="button"
                      className={detailDangerClass}
                      onClick={() => setDialog({
                        op: 'users.revoke_api_token',
                        params: { account_id: accountId, token_id: t.id },
                        title: `Revoke ${t.label ?? t.id}`,
                        critical: true,
                        onDone: refreshTokens,
                      })}
                    >
                      Revoke token
                    </button>
                  )}
                >
                  <KeyValue items={[
                    ['Created', <span className="font-mono">{date(t.created_at)}</span>],
                    ['Last access', <span className="font-mono">{date(t.last_access)}</span>],
                    ['Token id', <span className="font-mono text-[12px] text-text-2">{t.id}</span>],
                  ]} />
                </DetailPane>
              )}
            />
          </SectionBoundary>
        )}
        {tab === 'projects' && (
          <SectionBoundary key="projects" label="Projects" resetKeys={[projects]}>
            <ListSection data={projects} keyKind="jira" label="Permitted projects" columns={projectColumns}
              rowId={(p) => p.key} empty="No permitted projects." />
          </SectionBoundary>
        )}
        {tab === 'account' && (
          <SectionBoundary key="account" label="Account" onRetry={refreshOrg} resetKeys={[org]}>
            <div className="min-h-0 flex-1 overflow-y-auto px-4 py-3.5">
              <div className="grid max-w-[880px] gap-6 md:grid-cols-2">
                <DetailSection title="Org status">
                  {org === null ? <LoadingRows label="Loading org status" rows={3} />
                    : isErr(org) ? <p className="text-[13px] text-warn">Org status unavailable — {errText(org.error, 'org')}</p>
                    : (
                      <KeyValue items={[
                        ['Account type', <span className="font-mono">{org.account_type ?? '—'}</span>],
                        ['Status', org.account_status ? <StatusText status={org.account_status} /> : '—'],
                        ['Last active', <span className="font-mono">{org.last_active ? new Date(org.last_active).toLocaleString() : '—'}</span>],
                        ['Managed', org.managed ? 'yes' : 'no'],
                      ]} />
                    )}
                </DetailSection>
                <DetailSection title="Offboard — transfer assets">
                  <p className="mb-2 text-[12.5px] text-text-2">
                    Move this user’s owned filters and dashboards to another person before offboarding.
                  </p>
                  <Combobox
                    source="user"
                    valueKey="account_id"
                    deps={{}}
                    multi={false}
                    value={transferTarget}
                    onChange={setTransferTarget}
                    ariaLabel="Transfer to"
                    className=""
                    onSelect={(targetId) => {
                      if (targetId && targetId !== accountId) {
                        setDialog({
                          op: 'users.transfer_user_assets',
                          params: { source_account_id: accountId, target_account_id: targetId },
                          title: 'Transfer filters & dashboards',
                          critical: true,
                          onDone: () => setTransferTarget(''),
                        })
                      }
                    }}
                  />
                </DetailSection>
              </div>
            </div>
          </SectionBoundary>
        )}
      </div>

      {dialog && (
        <UserActionDialog
          op={dialog.op}
          params={dialog.params}
          title={dialog.title}
          critical={dialog.critical}
          onClose={() => setDialog(null)}
          onDone={dialog.onDone ?? refreshGroups}
        />
      )}
    </Page>
  )
}
